import { supabase } from '../lib/supabase';

/**
 * Real-time subscription service for Supabase
 * Manages subscriptions for user_profiles, helpers, helper_availability, and other tables
 */
export const realtimeService = {
  /**
   * Subscribe to user_profiles table changes
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToUserProfiles(callback) {
    const channel = supabase
      ?.channel('user_profiles_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            old: payload?.old,
            new: payload?.new
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to helpers table changes
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToHelpers(callback) {
    const channel = supabase
      ?.channel('helpers_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'helpers'
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            old: payload?.old,
            new: payload?.new
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to helper_availability table changes
   * @param {string} helperId - Optional helper ID to filter changes
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToHelperAvailability(callback, helperId = null) {
    const channelName = helperId ? `helper_availability_${helperId}` : 'helper_availability_changes';
    
    let channelBuilder = supabase
      ?.channel(channelName)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'helper_availability',
          ...(helperId && { filter: `helper_id=eq.${helperId}` })
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            old: payload?.old,
            new: payload?.new
          });
        }
      );

    const channel = channelBuilder?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to appointments table changes
   * @param {string} userId - Optional user ID to filter changes
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToAppointments(callback, userId = null) {
    const channelName = userId ? `appointments_${userId}` : 'appointments_changes';
    
    const channel = supabase
      ?.channel(channelName)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          ...(userId && { filter: `user_id=eq.${userId}` })
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            old: payload?.old,
            new: payload?.new
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to sessions table changes
   * @param {string} userId - Optional user ID to filter changes
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToSessions(callback, userId = null) {
    const channelName = userId ? `sessions_${userId}` : 'sessions_changes';
    
    const channel = supabase
      ?.channel(channelName)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sessions',
          ...(userId && { filter: `user_id=eq.${userId}` })
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            old: payload?.old,
            new: payload?.new
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to performance_metrics table changes (for admin monitoring)
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToPerformanceMetrics(callback) {
    const channel = supabase
      ?.channel('performance_metrics_changes')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'performance_metrics'
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            new: payload?.new
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Subscribe to wellbeing_scores table changes
   * @param {string} userId - User ID to filter changes
   * @param {Function} callback - Called with { eventType, old, new } on changes
   * @returns {Object} subscription object with unsubscribe method
   */
  subscribeToWellbeingScores(callback, userId) {
    const channel = supabase
      ?.channel(`wellbeing_scores_${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'wellbeing_scores',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            old: payload?.old,
            new: payload?.new
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    supabase?.removeAllChannels();
  }
};